import React from 'react';
import {extractDroppedUploadEntries} from './sourcesDropUtils';
import {
    createSnippetPayloadFromSourceFile,
    createSnippetPayloadsFromSourceFiles,
    createSourceItemDragPayload,
    createSourceItemsDragPayload,
    readSourceItemDragPayload,
    writeDragPayload,
} from './sourcesDragPayload';
import {applySourcesDragGhost} from './sourcesDragGhost';
import {useSourcesExplorerActions} from './useSourcesSectionStoreSelectors';

const useSourcesDragAndDrop = ({
    activeView,
    currentFolderId,
    isSharedPresetRoot,
    userId,
    selectedEntries,
    moveEntryToFolder,
    reloadActiveViewSilently,
    onFilesDropped,
}) => {
    const {setError} = useSourcesExplorerActions();

    const buildDragSelection = React.useCallback((itemType, item) => {
        const itemKey = `${itemType}:${String(item?.id)}`;
        const existingSelection = selectedEntries.some((entry) => entry.key === itemKey);

        if (existingSelection) {
            return selectedEntries;
        }

        return [{
            key: itemKey,
            itemType,
            id: String(item?.id || ''),
            parentFolderId: currentFolderId,
            name: item?.name || '',
        }];
    }, [currentFolderId, selectedEntries]);

    const handleDropUpload = React.useCallback(async (event, targetFolderId) => {
        if (!userId) {
            setError('An authenticated user is required to upload files.');
            return;
        }

        try {
            const entries = await extractDroppedUploadEntries(event?.dataTransfer);
            if (!entries.length) {
                return;
            }

            // Pass all dropped files to the dialog — it separates allowed from unsupported
            // and shows the rejection UX per file. No silent dropping here.
            onFilesDropped?.(entries.map((entry) => entry.file), targetFolderId || null);
            setError(null);
        } catch (dropError) {
            console.error('[SourcesSection] Failed to process dropped files:', dropError);
            setError(dropError?.message || 'Failed to read dropped files.');
        }
    }, [onFilesDropped, setError, userId]);

    const handleDropToCurrentView = React.useCallback((event) => {
        if (isSharedPresetRoot) {
            return;
        }
        handleDropUpload(event, currentFolderId);
    }, [currentFolderId, handleDropUpload, isSharedPresetRoot]);

    const handleDropToFolder = React.useCallback((folder, event) => {
        const draggedItem = readSourceItemDragPayload(event?.dataTransfer);
        if (draggedItem) {
            let targetFolderId = folder?.id || null;

            if (!userId) {
                setError('An authenticated user is required to move files and folders.');
                return;
            }

            if (activeView.type === 'root' && folder?.isPreset) {
                if (folder.id === 'shared' || folder.id === 'collected-materials' || folder.id === 'collected') {
                    return;
                }
                if (folder.id === 'my') {
                    targetFolderId = null;
                }
            }
            if (folder?.isSystem || activeView?.branch === 'collected-materials') {
                return;
            }

            const moveRequests = (draggedItem.items || []).filter((item) => {
                const sourceParentId = item.parentFolderId || null;
                if (!targetFolderId || String(targetFolderId) === String(item.id)) {
                    if (!targetFolderId && sourceParentId == null) {
                        return false;
                    }
                    if (targetFolderId) {
                        return false;
                    }
                }
                return (sourceParentId ?? null) !== (targetFolderId ?? null);
            });

            if (!moveRequests.length) {
                return;
            }

            Promise.all(moveRequests.map((item) => moveEntryToFolder(item, targetFolderId)))
                .then(() => {
                    setError(null);
                    return reloadActiveViewSilently();
                })
                .catch((moveError) => {
                    console.error('[SourcesSection] Failed to move source item:', moveError);
                    setError(moveError?.message || 'Failed to move source item.');
                });
            return;
        }

        if (activeView.type === 'root' && folder?.isPreset) {
            if (folder.id === 'shared' || folder.id === 'collected-materials' || folder.id === 'collected') {
                return;
            }
            if (folder.id === 'my') {
                handleDropUpload(event, null);
                return;
            }
        }
        if (folder?.isSystem || activeView?.branch === 'collected-materials') {
            return;
        }

        handleDropUpload(event, folder?.id || null);
    }, [activeView, handleDropUpload, moveEntryToFolder, reloadActiveViewSilently, setError, userId]);

    const canDropToFolder = React.useCallback((folder) => {
        if (folder?.isSystem) {
            return false;
        }
        if (activeView.type === 'root' && folder?.isPreset
            && (folder.id === 'shared' || folder.id === 'collected-materials' || folder.id === 'collected')) {
            return false;
        }
        if (activeView?.branch === 'collected-materials') {
            return false;
        }
        return true;
    }, [activeView.type, activeView?.branch]);

    const canDragFolder = React.useCallback((folder) => !folder?.isPreset && !folder?.isSystem, []);

    const handleFileDragStart = React.useCallback((event, file) => {
        const dragSelection = buildDragSelection('file', file);

        const dragItems = dragSelection.map((entry) => ({
            itemType: entry.itemType,
            id: entry.id,
            parentFolderId: entry.parentFolderId ?? currentFolderId ?? null,
            name: entry.name || '',
        }));

        const payload = createSourceItemsDragPayload(dragItems) || createSourceItemDragPayload({
            itemType: 'file',
            id: file?.id,
            parentFolderId: currentFolderId,
            name: file?.name,
        });

        const snippets = createSnippetPayloadsFromSourceFiles(
            dragSelection
                .filter((entry) => entry.itemType === 'file')
                .map((entry) => (
                    String(entry.id) === String(file?.id)
                        ? file
                        : {id: entry.id, name: entry.name}
                ))
        );
        writeDragPayload(
            event?.dataTransfer,
            payload,
            snippets.length ? snippets : createSnippetPayloadFromSourceFile(file)
        );

        const dragLabel = dragSelection.length > 1
            ? `${dragSelection.length} items`
            : dragSelection[0]?.name || file?.name || 'File';
        applySourcesDragGhost(event?.dataTransfer, dragLabel);
    }, [buildDragSelection, currentFolderId]);

    const handleFolderDragStart = React.useCallback((event, folder) => {
        const dragSelection = buildDragSelection('folder', folder);

        const payload = createSourceItemsDragPayload(dragSelection.map((entry) => ({
            itemType: entry.itemType,
            id: entry.id,
            parentFolderId: entry.parentFolderId ?? currentFolderId ?? null,
            name: entry.name || '',
        }))) || createSourceItemDragPayload({
            itemType: 'folder',
            id: folder?.id,
            parentFolderId: currentFolderId,
            name: folder?.name,
        });
        writeDragPayload(event?.dataTransfer, payload);

        const dragLabel = dragSelection.length > 1
            ? `${dragSelection.length} items`
            : dragSelection[0]?.name || folder?.name || 'Folder';
        applySourcesDragGhost(event?.dataTransfer, dragLabel);
    }, [buildDragSelection, currentFolderId]);

    return {
        handleDropToCurrentView,
        handleDropToFolder,
        canDropToFolder,
        canDragFolder,
        handleFileDragStart,
        handleFolderDragStart,
    };
};

export default useSourcesDragAndDrop;
